import { useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import Button from '@mui/material/Button'
import * as yup from 'yup'
import { alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AppBar from '@mui/material/AppBar'
import Divider from '@mui/material/Divider'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Avatar from '@mui/material/Avatar'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import IconButton from '@mui/material/IconButton'
import Container from '../../GeneralComponents/Container'
import { Topbar, Sidebar } from '../components'
import setGlobalLoader from '../../../redux/actions/loaderActions'
import { statesAndCities } from '../../../utils/indianStatesCities'
import {
  getOrganizationById,
  updateOrganization,
} from '../../../redux/actions/apiActions'
import { s3Upload } from '../../../utils/uploadService'
import { compressLogoImage, compressImageWithDimension } from '../../../utils/imageCompressor'
import { CircularProgress, FormControl } from '@mui/material'

const ATTENDANCE_MODES = {
  KM: 'KM',
  MINUTES: 'MINUTES'
}

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required.'),
  proprietorName: yup.string().trim().required('Proprietor Name is required.'),
  email: yup.string().trim().email('Invalid email format').optional().nullable(),
  phoneNumber: yup
    .string()
    .trim()
    .required('Phone Number is required.')
    .min(10, 'Phone number should be 10 digits.')
    .max(10, 'Phone number should be 10 digits.'),
  address: yup.string().trim().required('Address is required.'),
  city: yup.string().trim().required('City is required.'),
  state: yup.string().trim().required('State is required.'),
  pincode: yup.string().trim().required('Pincode is required.'),
  license: yup.string().trim().max(50, 'License number should not exceed 50 characters.'),
  licenseImg: yup.string().trim().max(200, 'License image URL should not exceed 200 characters.'),
  // Bank details validation
  bankAccountHolderName: yup.string().trim().max(100, 'Account holder name should not exceed 100 characters.').nullable(),
  bankAccountNumber: yup.string().trim().max(20, 'Account number should not exceed 20 characters.').nullable(),
  bankIFSCCode: yup.string().trim().max(11, 'IFSC code should be 11 characters.').nullable(),
  // Settings
  attendanceMode: yup.string().oneOf([ATTENDANCE_MODES.KM, ATTENDANCE_MODES.MINUTES]).nullable(),
  logo: yup.string().trim().nullable(),
  googleReviewLink: yup.string().trim().url('Please enter a valid URL').nullable(),
  certificateName: yup.string().trim().max(100, 'Certificate name should not exceed 100 characters.').nullable(),
})

const OrganizationProfile = () => {
  const dispatch = useDispatch()
  const [organizationDetails, setOrganizationDetails] = useState({})
  const theme = useTheme()
  const user = useSelector(state => state.authReducer.user)
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  const [selectedState, setSelectedState] = useState('')
  const [stateOptions, setStateOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [licenseImgPreview, setLicenseImgPreview] = useState(null)
  const [licenseImgFile, setLicenseImgFile] = useState(null)
  const fileInputRef = useRef(null)
  const licenseImgInputRef = useRef(null)

  useEffect(() => {
    setStateOptions(statesAndCities.map(item => ({ label: item.state, value: item.state })))
    if (selectedState && typeof selectedState === 'object' && selectedState.value) {
      const stateData = statesAndCities.find(state => state.state.toLowerCase() === selectedState.value.toLowerCase())
      if (stateData) {
        const cities = [...stateData.cities]
        cities.sort((a, b) => a.localeCompare(b))
        setCityOptions(cities.map(city => ({ label: city, value: city })))
      }
    }
  }, [selectedState])

  const [openSidebar, setOpenSidebar] = useState(false)

  const handleSidebarOpen = () => {
    setOpenSidebar(true)
  }

  const handleSidebarClose = () => {
    setOpenSidebar(false)
  }
  const open = isMd ? false : openSidebar

  useEffect(() => {
    if (user?._id) {
      dispatch(setGlobalLoader(true))
      async function fetchOrganization() {
        try {
          const orgRes = await getOrganizationById(user._id)
          setOrganizationDetails(orgRes?.data?.data)
          if (orgRes?.data?.data?.state) {
            setSelectedState({ label: orgRes.data.data.state, value: orgRes.data.data.state })
          }
          if (orgRes?.data?.data?.logo) {
            setLogoPreview(orgRes.data.data.logo)
          }
          if (orgRes?.data?.data?.licenseImg) {
            setLicenseImgPreview(orgRes.data.data.licenseImg)
          }
          dispatch(setGlobalLoader(false))
        } catch (error) {
          dispatch(setGlobalLoader(false))
          console.log('get organization details error', error)
          toast.error(
            error.response?.data?.msg ??
            'Error occurred while fetching organization details'
          )
        }
      }
      fetchOrganization()
    }
  }, [user?._id, dispatch])

  const handleImageSelect = (event, type) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp']
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or WebP)')
      return
    }

    // Validate file size (max 5MB)
    const maxSize = 5
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`Image size should be less than ${maxSize}MB`)
      return
    }

    // Create local preview
    const previewUrl = URL.createObjectURL(file)

    // Update state based on type
    if (type === 'logo') {
      setLogoFile(file)
      setLogoPreview(previewUrl)
    } else if (type === 'licenseImg') {
      setLicenseImgFile(file)
      setLicenseImgPreview(previewUrl)
    }
  }

  const handleOnSubmit = async values => {
    dispatch(setGlobalLoader(true))

    if (!values.email) {
      values.email = null
    }

    if (!values.license) {
      values.license = null
    }

    // Handle bank details - set to null if empty
    const bankFields = ['bankAccountHolderName', 'bankAccountNumber', 'bankIFSCCode']
    bankFields.forEach(field => {
      if (!values[field]) {
        values[field] = null
      }
    })

    // Handle settings fields - default to KM if no value
    if (!values.attendanceMode) {
      values.attendanceMode = ATTENDANCE_MODES.KM
    }

    try {
      // Upload logo if a new file was selected
      if (logoFile) {
        const compressedFile = await compressLogoImage(logoFile)
        const uploadResult = await s3Upload(compressedFile, {
          entity: 'organization',
          entityId: user._id
        })
        if (uploadResult && uploadResult[0]?.url) {
          values.logo = uploadResult[0].url
        }
      } else {
        delete values.logo
      }

      // Upload license image if a new file was selected
      if (licenseImgFile) {
        const compressedFile = await compressImageWithDimension(licenseImgFile, 1200, 0.9)
        const uploadResult = await s3Upload(compressedFile, {
          entity: 'organization',
          entityId: user._id
        })
        if (uploadResult && uploadResult[0]?.url) {
          values.licenseImg = uploadResult[0].url
        }
      } else {
        delete values.licenseImg
      }

      // Handle Google review link
      if (!values.googleReviewLink) {
        values.googleReviewLink = null
      }

      // Handle certificate name
      if (!values.certificateName) {
        values.certificateName = null
      }

      await updateOrganization(user._id, values)
      toast.success('Profile updated successfully')

      // Clear files after successful upload
      setLogoFile(null)
      setLicenseImgFile(null)

      dispatch(setGlobalLoader(false))
    } catch (error) {
      dispatch(setGlobalLoader(false))
      console.log('update organization error', error)
      toast.error(error.response?.data?.msg ?? 'Error occurred while updating profile.')
    }
  }

  return (
    <Box>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <br />
        <br />

        <Box overflow="hidden" marginLeft={{ md: '270px' }} paddingX={{ xs: 2, md: 4 }}>
          <Typography
            variant={'h5'}
            sx={{ marginBottom: 2 }}
            fontWeight={700}
          >
            Organization Profile
          </Typography>

          <Formik
            initialValues={{
              name: organizationDetails?.name || '',
              proprietorName: organizationDetails?.proprietorName || '',
              phoneNumber: organizationDetails?.phoneNumber || '',
              email: organizationDetails?.email || '',
              license: organizationDetails?.license || '',
              licenseImg: organizationDetails?.licenseImg || '',
              address: organizationDetails?.address || '',
              pincode: organizationDetails?.pincode || '',
              city: organizationDetails?.city || '',
              state: organizationDetails?.state || '',
              // Bank details
              bankAccountHolderName: organizationDetails?.bankAccountHolderName || '',
              bankAccountNumber: organizationDetails?.bankAccountNumber || '',
              bankIFSCCode: organizationDetails?.bankIFSCCode || '',
              // Settings
              attendanceMode: [ATTENDANCE_MODES.KM, ATTENDANCE_MODES.MINUTES].includes(organizationDetails?.attendanceMode)
                ? organizationDetails.attendanceMode
                : ATTENDANCE_MODES.KM,
              logo: organizationDetails?.logo || '',
              googleReviewLink: organizationDetails?.googleReviewLink || '',
              certificateName: organizationDetails?.certificateName || '',
            }}
            validationSchema={validationSchema}
            onSubmit={values => handleOnSubmit(values)}
            enableReinitialize
          >
            {formikProps => {
              const {
                values,
                errors,
                touched,
                setFieldValue,
                handleSubmit,
                isSubmitting,
              } = formikProps
              return (
                <form onSubmit={handleSubmit}>
                  {/* Organization Settings Section */}
                  <Typography
                    variant={'subtitle1'}
                    sx={{ marginBottom: 2, marginTop: 2 }}
                    fontWeight={600}
                    color="primary"
                  >
                    Organization Settings
                  </Typography>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    {/* Logo Upload */}
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Organization Logo
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={logoPreview || values.logo}
                          sx={{
                            width: 100,
                            height: 100,
                            border: '2px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {values.name?.charAt(0)?.toUpperCase() || 'O'}
                        </Avatar>
                        <Box>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={fileInputRef}
                            onChange={(e) => handleImageSelect(e, 'logo')}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            size="small"
                          >
                            {(logoFile || logoPreview || values.logo) ? 'Change Logo' : 'Select Logo'}
                          </Button>
                          {logoFile && (
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                              New logo selected. Click "Update Profile" to save.
                            </Typography>
                          )}
                          <Typography variant="caption" display="block" sx={{ mt: logoFile ? 0.5 : 1, color: 'text.secondary' }}>
                            Max size: 5MB. Formats: JPEG, PNG, WebP
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            For best results on invoices: square image (e.g. 200×200 px), PNG with transparent background preferred.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Attendance Mode */}
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Attendance Recording Mode
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                        Choose how attendance will be recorded for students
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          row
                          value={values.attendanceMode}
                          onChange={(e) => setFieldValue('attendanceMode', e.target.value)}
                        >
                          <FormControlLabel
                            value={ATTENDANCE_MODES.KM}
                            control={<Radio />}
                            label="Kilometers (KM)"
                          />
                          <FormControlLabel
                            value={ATTENDANCE_MODES.MINUTES}
                            control={<Radio />}
                            label="Minutes (Min)"
                          />
                        </RadioGroup>
                      </FormControl>
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'warning.main' }}>
                        For this to take effect, the user on mobile app should logout and login again.
                      </Typography>
                    </Grid>

                    {/* Google Review Link */}
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Google Review Link
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                        Add your Google Business review link for students to rate you
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TextField
                          variant="outlined"
                          name={'googleReviewLink'}
                          fullWidth
                          size="small"
                          placeholder="https://g.page/r/your-business-link"
                          value={values.googleReviewLink}
                          onChange={e => setFieldValue('googleReviewLink', e.target.value)}
                          error={touched.googleReviewLink && Boolean(errors.googleReviewLink)}
                          helperText={touched.googleReviewLink && errors.googleReviewLink}
                        />
                        <IconButton
                          size="small"
                          disabled={!values.googleReviewLink}
                          onClick={() => window.open(values.googleReviewLink, '_blank')}
                          sx={{
                            mt: 0.5,
                            color: values.googleReviewLink ? 'primary.main' : 'text.disabled',
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Box>
                    </Grid>

                    {/* Certificate Name */}
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Certificate Name
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'certificateName'}
                        fullWidth
                        size="small"
                        placeholder="e.g., Drivolo Driving School"
                        value={values.certificateName}
                        onChange={e => setFieldValue('certificateName', e.target.value)}
                        error={touched.certificateName && Boolean(errors.certificateName)}
                        helperText={
                          (touched.certificateName && errors.certificateName) ||
                          'This name will be printed on the student certificate. Leave blank to use the original name.'
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* Basic Information Section */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography
                    variant={'subtitle1'}
                    sx={{ marginBottom: 2 }}
                    fontWeight={600}
                    color="primary"
                  >
                    Basic Information
                  </Typography>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Driving School Name *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'name'}
                        fullWidth
                        size="small"
                        value={values.name}
                        onChange={e => setFieldValue('name', e.target.value)}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Proprietor Name *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'proprietorName'}
                        fullWidth
                        size="small"
                        value={values.proprietorName}
                        onChange={e => setFieldValue('proprietorName', e.target.value)}
                        error={touched.proprietorName && Boolean(errors.proprietorName)}
                        helperText={touched.proprietorName && errors.proprietorName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Phone Number *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'phoneNumber'}
                        fullWidth
                        size="small"
                        type="number"
                        value={values.phoneNumber}
                        onChange={e =>
                          e.target.value.toString().length > 10
                            ? setFieldValue(
                              'phoneNumber',
                              e.target.value.substring(0, 10)
                            )
                            : setFieldValue('phoneNumber', e.target.value)
                        }
                        error={
                          touched.phoneNumber && Boolean(errors.phoneNumber)
                        }
                        helperText={touched.phoneNumber && errors.phoneNumber}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Email
                      </Typography>
                      <TextField
                        type='email'
                        variant="outlined"
                        name={'email'}
                        fullWidth
                        size="small"
                        value={values.email}
                        onChange={e => setFieldValue('email', e.target.value)}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        License Number
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'license'}
                        fullWidth
                        size="small"
                        type="text"
                        value={values.license}
                        onChange={e =>
                          setFieldValue('license', e.target.value.toString())
                        }
                        error={touched.license && Boolean(errors.license)}
                        helperText={touched.license && errors.license}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        MDS Code
                      </Typography>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={organizationDetails?.displayId || '-'}
                        InputProps={{ readOnly: true }}
                        helperText="System-generated identifier. Cannot be edited."
                        sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        License Image
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {(licenseImgPreview || values.licenseImg) && (
                          <Box
                            component="img"
                            src={licenseImgPreview || values.licenseImg}
                            sx={{
                              width: 120,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '2px solid',
                              borderColor: 'divider',
                            }}
                          />
                        )}
                        <Box>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={licenseImgInputRef}
                            onChange={(e) => handleImageSelect(e, 'licenseImg')}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => licenseImgInputRef.current?.click()}
                            size="small"
                          >
                            {(licenseImgFile || licenseImgPreview || values.licenseImg) ? 'Change Image' : 'Select Image'}
                          </Button>
                          {licenseImgFile && (
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                              New image selected. Click "Update Profile" to save.
                            </Typography>
                          )}
                          <Typography variant="caption" display="block" sx={{ mt: licenseImgFile ? 0.5 : 1, color: 'text.secondary' }}>
                            Max size: 5MB. Formats: JPEG, PNG, WebP
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Address Section */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography
                    variant={'subtitle1'}
                    sx={{ marginBottom: 2 }}
                    fontWeight={600}
                    color="primary"
                  >
                    Address Details
                  </Typography>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Address *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'address'}
                        fullWidth
                        size="small"
                        value={values.address}
                        onChange={e => setFieldValue('address', e.target.value)}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        State *
                      </Typography>
                      <FormControl
                        fullWidth
                        error={touched.state && Boolean(errors.state)}
                      >
                        <Select
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 }),
                            control: provided => ({ ...provided, minHeight: '40px' })
                          }}
                          placeholder="Select State"
                          value={(values.state !== null && values.state !== undefined && values.state !== '') ? stateOptions.find(d => d.value === values.state) : null}
                          options={stateOptions}
                          onChange={(e) => {
                            setSelectedState(e)
                            setFieldValue('state', e.value)
                            setFieldValue('city', '')
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        City *
                      </Typography>
                      <FormControl
                        fullWidth
                        error={touched.city && Boolean(errors.city)}
                      >
                        <Select
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 }),
                            control: provided => ({ ...provided, minHeight: '40px' })
                          }}
                          placeholder="Select City"
                          value={(values.city !== null && values.city !== undefined && values.city !== '') ? cityOptions.find(d => d.value === values.city) : null}
                          options={cityOptions}
                          isDisabled={!selectedState && !values.state}
                          onChange={(e) => {
                            setFieldValue('city', e.value)
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Pincode *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'pincode'}
                        fullWidth
                        size="small"
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        value={values.pincode}
                        onChange={e => setFieldValue('pincode', e.target.value)}
                        error={touched.pincode && Boolean(errors.pincode)}
                        helperText={touched.pincode && errors.pincode}
                      />
                    </Grid>
                  </Grid>

                  {/* Bank Details Section */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography
                    variant={'subtitle1'}
                    sx={{ marginBottom: 2 }}
                    fontWeight={600}
                    color="primary"
                  >
                    Bank Details
                  </Typography>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Account Holder Name
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'bankAccountHolderName'}
                        fullWidth
                        size="small"
                        placeholder="Name as per bank records"
                        value={values.bankAccountHolderName}
                        onChange={e => setFieldValue('bankAccountHolderName', e.target.value)}
                        error={touched.bankAccountHolderName && Boolean(errors.bankAccountHolderName)}
                        helperText={touched.bankAccountHolderName && errors.bankAccountHolderName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Account Number
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'bankAccountNumber'}
                        fullWidth
                        size="small"
                        placeholder="Enter account number"
                        value={values.bankAccountNumber}
                        onChange={e => setFieldValue('bankAccountNumber', e.target.value.replace(/\D/g, ''))}
                        error={touched.bankAccountNumber && Boolean(errors.bankAccountNumber)}
                        helperText={touched.bankAccountNumber && errors.bankAccountNumber}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        IFSC Code
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'bankIFSCCode'}
                        fullWidth
                        size="small"
                        placeholder="e.g., SBIN0001234"
                        value={values.bankIFSCCode}
                        onChange={e => setFieldValue('bankIFSCCode', e.target.value.toUpperCase())}
                        error={touched.bankIFSCCode && Boolean(errors.bankIFSCCode)}
                        helperText={touched.bankIFSCCode && errors.bankIFSCCode}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ marginTop: 4, marginBottom: 4 }}>
                    <Button
                      component={'button'}
                      variant={'contained'}
                      fontWeight={700}
                      type="submit"
                      disabled={isSubmitting}
                      size="large"
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'Update Profile'}
                    </Button>
                  </Box>
                </form>
              )
            }}
          </Formik>
        </Box>
      </main>
    </Box>
  )
}

export default OrganizationProfile
